<template>

  <form id="activesTypeForm" class="mx-auto col-sm-8" @submit.prevent>

    <div class="form-group" :class="{ 'has-error': errors.has('active_type') }">
      <label for="active_type">{{ $t('active_type') }}</label>
      <input
        type="text"
        id="active_type"
        name="active_type"
        class="form-control"
        :placeholder="$t('ph_active_type')"
        v-model="activeType.name"
        v-validate="'required'"
        @input="upper('name')"
      >

      <small v-show="errors.has('active_type')" class="form-text">
        {{ errors.first('active_type') }}
      </small>
    </div>

    <div class="form-group">
      <label for="prefix">{{ $t('prefix') }}</label>
      <input
        type="text"
        id="prefix"
        name="prefix"
        class="form-control"
        :placeholder="$t('ph_prefix')"
        v-model="activeType.prefix"
        @input="upper('prefix')"
      >
      
      <small v-show="errors.has('prefix')" class="form-text">
        {{ errors.first('prefix') }}
      </small>
    </div>

    <div class="form-group">
      <label for="location">{{ $t('location') }}</label>
      <Combobox
        class="bg-white"
        name='location'
        :hasErrors="errors.has('location')"
        :placeholder="$t('select_a_item')"
        label="name"
        :options="locations"
        :initial-value="activeType.location"
        v-model="activeType.location"
        v-validate="'required'"
      />

      <small v-show="errors.has('location')" class="form-text">
        {{ errors.first('location') }}
      </small>
    </div>

    <div class="form-group">
      <label for="status">{{ $t('status') }}</label>
      <Combobox
        :disabled="!$route.params.id"
        class="bg-white"
        name='status'
        :placeholder="$t('select_a_item')"
        label="name"
        :options="activestatus"
        :getOptionLabel="status => status.status ? status.status.name : ''"
        :initial-value="activeType.status"
        v-model="activeType.status"
        :reduce="activeStatus => activeStatus.id"
      />
    </div>

    <div class="form-group">
      <label for="field_legend1">{{ $t('field_legend') }} 1</label>
      <input
        type="text"
        id="field_legend1"
        name="field_legend1"
        class="form-control"
        :placeholder="$t('ph_field_legend') + ' 1'"
        v-model="activeType.fieldLegend1"
        @input="upper('fieldLegend1')"
      >
    </div>

    <div class="form-group">
      <label for="field_legend2">{{ $t('field_legend') }} 2</label>
      <input
        type="text"
        id="field_legend2"
        name="field_legend2"
        class="form-control"
        :placeholder="$t('ph_field_legend') + ' 2'"
        v-model="activeType.fieldLegend2"
        @input="upper('fieldLegend2')"
      >
    </div>

    <div class="form-group">
      <label for="field_legend3">{{ $t('field_legend') }} 3</label>
      <input
        type="text"
        id="field_legend3"
        name="field_legend3"
        class="form-control"
        :placeholder="$t('ph_field_legend') + ' 3'"
        v-model="activeType.fieldLegend3"
        @input="upper('fieldLegend3')"
      >
    </div>

    <div class="switch mb-3">
      <label for="isActive">{{ $t('active') }}</label>
      <div class="onoffswitch2">
        <input
          type="checkbox"
          class="onoffswitch2-checkbox"
          id="isActive"
          v-model="activeType.isActive">
        <label class="onoffswitch2-label" for="isActive">
            <span class="onoffswitch2-inner"></span>
            <span class="onoffswitch2-switch"></span>
        </label>
      </div>
    </div>

    <div class="form-group row">
      <div class="col-sm-4 col-sm-offset-2">
        <LoaderButton @click="onClick">
          {{ $t('save') }}
        </LoaderButton>
      </div>
    </div>

  </form>

</template>

<script>
import { mapState, mapActions } from 'vuex'
import LoaderButton from '@/components/button/LoaderButton'
import Combobox from '@/components/shared/ComboBox'
import location from '@/config/locations'

export default {
  name: 'ActivesTypeForm',
  components: {
    LoaderButton,
    Combobox
  },

  data() {
    return {
      locations: location
    }
  },
  
  props: {
    activeType: {
      type: Object,
      default: () => ({
        id: undefined,
        name: '',
        prefix: '',
        location: '',
        status: undefined,
        fieldLegend1: '',
        fieldLegend2: '',
        fieldLegend3: '',
        isActive: true
      })
    }
  },

  methods: {
    ...mapActions('activestype', [
      'create',
      'edit',
      'cleanState'
    ]),

    async onClick (evtClick, doneFn) {
      const isValid = await this.$validator.validateAll()
      if (!isValid) {
        const err = new Error(this.$t('invalid_form_fields'))
        this.$store.dispatch('notify/error', err)
        return
      }
      
      const { id } = this.activeType
      
      const result = id
        ? await this.edit({ id, data: this.activeType })
        : await this.create(this.activeType)

      if (result) {
        return this.$router.push({ name: 'listactivestypes' })
      }
    },

    upper(attr) {
      this.activeType[attr] = this.activeType[attr].toUpperCase()
    }
  },

  computed: {
    ...mapState({
      activestype: state => state.activestype.activesTypeById,
      activestatus: state => state.activestatus.all
    })
  },

  async mounted() {
    const { id } = this.$route.params

    if (id) {
      await this.$store.dispatch('activestatus/fetchFiltered', { activeType: id, isForm: true })
      
      const {
        name,
        prefix,
        location,
        status,
        isActive,
        fieldLegend1,
        fieldLegend2,
        fieldLegend3
      } = this.activestype

      this.activeType.id = id
      this.activeType.name = name
      this.activeType.prefix = prefix === null ? undefined : prefix
      this.activeType.location = location === null ? '' : location
      this.activeType.status = status === null ? undefined : status
      this.activeType.fieldLegend1 = fieldLegend1 === null ? undefined : fieldLegend1
      this.activeType.fieldLegend2 = fieldLegend2 === null ? undefined : fieldLegend2
      this.activeType.fieldLegend3 = fieldLegend3 === null ? undefined : fieldLegend3
      this.activeType.isActive = isActive
    }
  },

  destroyed () {
    this.cleanState()
    this.$store.dispatch('activestatus/cleanState')
  }
}
</script>